/**
 * Сверка опубликованного с одобренным. §Ключ 6.
 *
 * ОДНА функция, ОДИН алгоритм — посимвольная карта атрибутов. Сравнение отсортированных
 * списков кортежей (02-contracts.md §8 и 07-publisher.md §6) удалено: оно даёт ложные
 * расхождения на любом посте, где форматирование пересекается со ссылкой, упоминанием или
 * хэштегом. Сервер режет наш {italic 0,13} на три куска — списки не совпадут, карта совпадёт.
 *
 * expectedServerEntities() в сверке НЕ участвует: после вычитания автоопределяемых типов
 * карта из expected совпадает с картой из canonical.entities посимвольно, а лишний вызов
 * поставил бы вердикт сверки в зависимость от версии НАШЕГО детектора ссылок.
 */

import type { CanonicalDraft } from './draft.ts';
import {
  AUTO_DETECTED_ENTITY_TYPES,
  isAutoDetectedEntityType,
  SENDABLE_ENTITY_TYPES,
} from './entity.ts';
import type { IncomingEntity, TelegramMessage } from './message.ts';
import {
  type FlatEntity,
  flattenRich,
  richDocumentPlacement,
  richFromCanonical,
  richPhotoIsFirst,
} from './rich.ts';

/** Ключ атрибута: тип и его параметры. Непрозрачен, сравнивается только строкой. */
export type AttrKey = string;

const SEP = ' ';

interface AnyEntity {
  type: string;
  offset: number;
  length: number;
  url?: string | undefined;
  language?: string | undefined;
  custom_emoji_id?: string | undefined;
  user?: { id: number } | undefined;
}

/**
 * Кодирование AttrKey нормативно. Разделитель U+0000 — он не встречается ни в URL,
 * ни в имени языка, ни в идентификаторах.
 */
export function attrKeyOf(e: AnyEntity): AttrKey {
  switch (e.type) {
    case 'pre':
      return `pre${SEP}${e.language ?? ''}`;
    case 'text_link':
      return `text_link${SEP}${e.url ?? ''}`;
    case 'text_mention':
      return `text_mention${SEP}${String(e.user?.id ?? '')}`;
    case 'custom_emoji':
      return `custom_emoji${SEP}${e.custom_emoji_id ?? ''}`;
    default:
      return e.type;
  }
}

const typeOfAttrKey = (k: AttrKey): string => {
  const i = k.indexOf(SEP);
  return i < 0 ? k : k.slice(0, i);
};

/**
 * Массив длиной text.length (UTF-16 code units); на позиции i — множество активных атрибутов.
 *
 * Функция ТОТАЛЬНА: сущность с offset+length > text.length не бросает, а обрезается по длине
 * текста. Вернувшееся от сервера является авторитетом — его нельзя «не принять».
 */
export function attributeMap(
  text: string,
  entities: readonly AnyEntity[],
): { map: Array<Set<AttrKey>>; clamped: AnyEntity[] } {
  const map: Array<Set<AttrKey>> = new Array(text.length);
  for (let i = 0; i < text.length; i++) map[i] = new Set<AttrKey>();

  const clamped: AnyEntity[] = [];
  for (const e of entities) {
    const from = Math.max(0, e.offset);
    const to = Math.min(text.length, e.offset + e.length);
    if (e.offset < 0 || e.offset + e.length > text.length) clamped.push(e);
    const key = attrKeyOf(e);
    for (let i = from; i < to; i++) (map[i] as Set<AttrKey>).add(key);
  }
  return { map, clamped };
}

export type DeviationKind =
  | 'text_mismatch'
  | 'attributes_mismatch'
  | 'media_missing'
  | 'forbidden_entity_returned'
  | 'chat_mismatch'
  | 'entity_out_of_range'
  | 'link_preview_mismatch'
  | 'rich_message_missing'
  | 'rich_message_unexpected'
  | 'rich_block_unknown'
  | 'media_not_first'
  | 'document_missing'
  | 'document_not_last';

export interface Deviation {
  kind: DeviationKind;
  at?: number;
  expected: unknown;
  actual: unknown;
  note?: string;
}

export interface DeviationReport {
  ok: boolean;
  deviations: Deviation[];
  notes: string[];
  /** Позиция первого расхождения атрибутов; null при ok. Ложится в publication_verification. */
  first_diff_at: number | null;
}

const AUTO_SET: ReadonlySet<string> = new Set(AUTO_DETECTED_ENTITY_TYPES);
const SENDABLE_SET: ReadonlySet<string> = new Set(SENDABLE_ENTITY_TYPES);

/** Из множества удаляются атрибуты автоопределяемых типов — из ОБЕИХ карт. */
function stripAuto(set: Set<AttrKey>): Set<AttrKey> {
  const out = new Set<AttrKey>();
  for (const k of set) {
    if (!AUTO_SET.has(typeOfAttrKey(k))) out.add(k);
  }
  return out;
}

/**
 * Шаг 4а, ТОЛЬКО у rich-сообщения: ссылка, которой мы не ставили, — автоопределение.
 *
 * Узел `url` в ответе двузначен: им сервер возвращает и НАШУ ссылку (`text_link`), и ссылку,
 * найденную в тексте сам, — голый адрес или домен без схемы (`geoboost.ru` линкуется,
 * docs/telegram.md §3). `flattenRich` отдаёт обе как `text_link`: по дереву их не различить.
 * Различает подписанное. На позиции, где мы ставили ссылку, ответ сравнивается как есть — там
 * обязана стоять ровно наша, и потерянная или подменённая остаётся расхождением. Где не ставили,
 * ссылка из ответа — тип `url`, и шаг 4 вычитает её, как вычитает у `sendMessage`.
 *
 * У `sendMessage` так нельзя и незачем: там сервер сам различает `url` и `text_link`, и
 * `text_link`, которой мы не ставили, — настоящее расхождение.
 */
function autoLinksWhereWeSentNone(actual: Set<AttrKey>, expected: Set<AttrKey>): Set<AttrKey> {
  for (const k of expected) {
    if (typeOfAttrKey(k) === 'text_link') return actual;
  }
  const out = new Set<AttrKey>();
  for (const k of actual) out.add(typeOfAttrKey(k) === 'text_link' ? 'url' : k);
  return out;
}

const sortedKeys = (s: Set<AttrKey>): string[] => [...s].map((k) => k.replaceAll(SEP, ' ')).sort();

const sameSet = (a: Set<AttrKey>, b: Set<AttrKey>): boolean => {
  if (a.size !== b.size) return false;
  for (const k of a) {
    if (!b.has(k)) return false;
  }
  return true;
};

const EMPTY_SKIP: ReadonlySet<number> = new Set();

/**
 * Позиции обоих символов каждого `\n\n` — ровно то, что `richFromCanonical` использует как
 * границу блока. Считается по ПОДПИСАННОМУ тексту: он авторитет, а не то, что вернул сервер.
 */
function paragraphSeparatorPositions(text: string): ReadonlySet<number> {
  const out = new Set<number>();
  let i = text.indexOf('\n\n');
  while (i >= 0) {
    out.add(i);
    out.add(i + 1);
    i = text.indexOf('\n\n', i + 2);
  }
  return out;
}

/**
 * Плоская форма того, что МЫ ОТПРАВИЛИ rich-сообщением (19.09.2026). Дерево выводится из
 * подписанного содержания детерминированно (`richFromCanonical`), и тот же разбор
 * (`flattenRich`) применяется к нему и к ответу — сравниваются две плоские формы одного
 * происхождения. Канонический текст напрямую сравнивать нельзя: цитаты и код уходят блоками, и
 * переводы строк на стыке абзаца и блока не отправляются (`rich.ts`, шапка).
 *
 * Функция тотальна, как и вся сверка: пост уже в канале. Содержание, которое дерево не
 * выражает (заморозка такое отбивает, но сверка не вправе на это полагаться), сравнивается
 * по каноническому тексту — расхождение тогда и будет сигналом.
 *
 * Экспортируется ради второго потребителя — пробы неопределённой публикации в публикаторе
 * (`processResolution`, находка publisher-wire-2): у форварда rich-поста тоже нет ни `text`, ни
 * `caption`, только дерево, и искать пост надо по той же плоской форме, а не по
 * каноническому тексту. Второй вывод «почти такой же» однажды разошёлся бы с этим.
 */
export function sentRichFlat(canonical: CanonicalDraft): {
  readonly text: string;
  readonly entities: readonly FlatEntity[];
} {
  try {
    const sent = richFromCanonical(
      canonical,
      canonical.media !== null ? 'attach://photo' : null,
      (canonical.document ?? null) !== null ? 'attach://document' : null,
    );
    const flat = flattenRich(sent.blocks);
    return { text: flat.text, entities: flat.entities };
  } catch {
    return { text: canonical.text, entities: canonical.entities };
  }
}

const LPO_FIELDS = [
  'is_disabled',
  'url',
  'prefer_small_media',
  'prefer_large_media',
  'show_above_text',
] as const;

const LPO_DEFAULTS: Record<string, unknown> = {
  is_disabled: false,
  url: null,
  prefer_small_media: false,
  prefer_large_media: false,
  show_above_text: false,
};

/** Узкая проверка формы: всё, что приехало от Telegram, — недоверенный `unknown`. */
function isRecord(v: unknown): v is Record<string, unknown> {
  return typeof v === 'object' && v !== null;
}

export function compareSentMessage(
  canonical: CanonicalDraft,
  sent: TelegramMessage,
  ctx: { chatId: string },
): DeviationReport {
  const deviations: Deviation[] = [];
  const notes: string[] = [];
  let firstDiffAt: number | null = null;

  // 1 — канал. Дальше не идём: сравнивать пост из чужого чата бессмысленно.
  if (String(sent.chat.id) !== ctx.chatId) {
    deviations.push({ kind: 'chat_mismatch', expected: ctx.chatId, actual: String(sent.chat.id) });
    return { ok: false, deviations, notes, first_diff_at: null };
  }

  // 2а — ПЛОСКАЯ ФОРМА ОТВЕТА. У rich-сообщения в ответе нет ни текста, ни сущностей:
  // есть дерево блоков, и плоскую форму из него добывает flattenRich. Дерево при этом
  // НЕ сравнивается с отправленным — сервер его нормализует (разворачивает массив из
  // одного узла, дописывает умолчания, подменяет ссылку на файл лесенкой PhotoSize),
  // и литеральное сравнение краснело бы на каждом посте.
  //
  // Способ отправки берётся из ПОДПИСАННОГО содержания, а не выводится заново (19.09.2026):
  // с политикой бренда «все посты — расширенным сообщением» короткий пост без картинки
  // законно уходит rich, и вывод по длине сверял бы его как обычное сообщение.
  const wire = canonical.method;
  const rich: unknown = (sent as unknown as Record<string, unknown>)['rich_message'];
  let actualText: string;
  let incomingEntities: readonly IncomingEntity[];
  let expectedText: string = canonical.text;
  let expectedEntities: readonly FlatEntity[] = canonical.entities;

  if (wire === 'sendRichMessage') {
    if (!isRecord(rich)) {
      deviations.push({
        kind: 'rich_message_missing',
        expected: 'rich_message',
        actual: 'отсутствует',
      });
      return { ok: false, deviations, notes, first_diff_at: null };
    }
    const flat = flattenRich(rich['blocks']);
    actualText = flat.text;
    // Находки автоопределения (хэштег, @упоминание, почта…) приходят сюда сущностями своих
    // типов, а не неизвестными узлами (`rich.ts`, шапка), и шаг 4 вычитает их из обеих карт.
    incomingEntities = flat.entities as readonly IncomingEntity[];
    for (const t of flat.unknown_node_types) {
      deviations.push({ kind: 'rich_block_unknown', expected: 'известный тип узла', actual: t });
    }
    const sentFlat = sentRichFlat(canonical);
    expectedText = sentFlat.text;
    expectedEntities = sentFlat.entities;
  } else {
    if (isRecord(rich)) {
      deviations.push({
        kind: 'rich_message_unexpected',
        expected: 'обычное сообщение',
        actual: 'rich_message',
      });
    }
    actualText = sent.text ?? sent.caption ?? '';
    incomingEntities = sent.entities ?? sent.caption_entities ?? [];
  }

  // 2 — текст побайтово. Здесь равенство ДОСТИЖИМО, любое расхождение — настоящий баг.
  if (expectedText !== actualText) {
    let at = 0;
    while (
      at < expectedText.length &&
      at < actualText.length &&
      expectedText[at] === actualText[at]
    ) {
      at++;
    }
    deviations.push({
      kind: 'text_mismatch',
      at,
      expected: expectedText.slice(Math.max(0, at - 20), at + 20),
      actual: actualText.slice(Math.max(0, at - 20), at + 20),
    });
  }

  // 3 — карты атрибутов.
  const incoming: readonly IncomingEntity[] = incomingEntities;
  const expected = attributeMap(expectedText, expectedEntities);
  const actual = attributeMap(actualText, incoming);

  if (actual.clamped.length > 0) {
    deviations.push({
      kind: 'entity_out_of_range',
      expected: `entities within 0..${actualText.length}`,
      actual: actual.clamped.map((e) => `${e.type} ${e.offset}+${e.length}`),
      note: 'сущность из ответа выходит за границы текста и была обрезана',
    });
  }

  // 4 — вычесть автоопределяемые типы из ОБЕИХ карт, 5 — сравнить позиция за позицией.
  //
  // У rich-сообщения из сравнения исключаются ДВА символа каждого разделителя абзацев.
  // До 16.09.2026 текст уходил блоками по абзацам, `\n\n` лежал МЕЖДУ блоками, и сущность,
  // накрывавшая разделитель, после круга неизбежно теряла его. Теперь текст уходит одним
  // блоком (`richFromCanonical`) и разметку на разделителе несёт, но исключение оставлено
  // как допуск: если сервер вернёт текст несколькими блоками, атрибут на двух невидимых
  // символах не должен поднимать critical `publish_deviation` — ровно тот сорт вечного
  // ложного алерта, из-за которого перестают читать алерты (см. шаг 8а ниже). Сам текст
  // по-прежнему сравнивается побайтово выше.
  //
  // Исключение узкое намеренно: у `sendMessage` разделитель разметку НЕСЁТ, и расхождение
  // на нём — настоящий баг. Поэтому набор пуст во всех случаях, кроме rich.
  //
  // Шаг 4а — тоже только у rich: ссылку, которую сервер нашёл в тексте сам, дерево возвращает
  // тем же узлом `url`, что и нашу (`autoLinksWhereWeSentNone`).
  const isRich = wire === 'sendRichMessage';
  const skip = isRich ? paragraphSeparatorPositions(expectedText) : EMPTY_SKIP;
  const n = Math.min(expected.map.length, actual.map.length);
  for (let i = 0; i < n; i++) {
    if (skip.has(i)) continue;
    const want = expected.map[i] as Set<AttrKey>;
    const got = actual.map[i] as Set<AttrKey>;
    const e = stripAuto(want);
    const a = stripAuto(isRich ? autoLinksWhereWeSentNone(got, want) : got);
    if (!sameSet(e, a)) {
      firstDiffAt = i;
      deviations.push({
        kind: 'attributes_mismatch',
        at: i,
        expected: sortedKeys(e),
        actual: sortedKeys(a),
      });
      break;
    }
  }

  // 6 — медиа: факт наличия И ВЁРСТКА. Сравнивать наш PNG с массивом PhotoSize по-прежнему
  // бессмысленно, а вот «картинка первым блоком» — правило вёрстки (решение 06.09.2026), и
  // оно проверяемо: сервер спокойно принимает фото последним блоком (проба C2), так что
  // ничего, кроме этой проверки, порядок не держит.
  if (canonical.media !== null) {
    const blocks: unknown = isRecord(rich) ? rich['blocks'] : undefined;
    if (richPhotoIsFirst(blocks)) {
      // порядок верный, картинка на месте
    } else if (Array.isArray(blocks) && blocks.some((b) => isRecord(b) && b['type'] === 'photo')) {
      const at = blocks.findIndex((b) => isRecord(b) && b['type'] === 'photo');
      deviations.push({
        kind: 'media_not_first',
        expected: 'блок photo первым',
        actual: `блок photo на позиции ${String(at)}`,
        at,
      });
    } else {
      deviations.push({ kind: 'media_missing', expected: 'блок photo', actual: 'его нет' });
    }
  }

  // 6а — документ (§9.2): последним блоком, как и отправлен. Имя файла сверяется ЗАПИСЬЮ,
  // а не расхождением: сервер вправе нормализовать имя (как и адрес карточки, шаг 8а), и
  // critical на каждом посте с файлом научил бы алерт не читать.
  // `?? null`: содержание, подписанное до 19.09.2026, поля не несёт — документа у него нет.
  const document = canonical.document ?? null;
  if (document !== null) {
    const place = richDocumentPlacement(isRecord(rich) ? rich['blocks'] : undefined);
    if (place.at === 'missing') {
      deviations.push({ kind: 'document_missing', expected: 'блок document', actual: 'его нет' });
    } else if (place.at === 'elsewhere') {
      deviations.push({
        kind: 'document_not_last',
        expected: 'блок document последним',
        actual: `блок document на позиции ${String(place.index)}`,
        at: place.index,
      });
    }
    if (place.fileName !== null && place.fileName !== document.file_name) {
      notes.push(`имя файла в ответе «${place.fileName}», подписано «${document.file_name}»`);
    }
  }

  // 7 — запрещённые типы в ответе. Проверка ЖИВАЯ именно потому, что STRIPPED_BY_SERVER_TYPES
  // на шаге 4 не вычитались: сервер обязан их вырезать, и их появление — сигнал.
  for (const e of incoming) {
    if (!SENDABLE_SET.has(e.type) && !isAutoDetectedEntityType(e.type)) {
      deviations.push({
        kind: 'forbidden_entity_returned',
        expected: 'sendable or auto-detected type',
        actual: e.type,
        at: e.offset,
      });
    }
  }

  // 8 — link_preview_options. Пропускается целиком у rich-сообщения: параметра карточки у
  // этого метода не существует вовсе — ни при картинке, ни при длинном тексте.
  if (wire === 'sendMessage') {
    const got = sent.link_preview_options;
    if (got === undefined) {
      // 8б. Отсутствие НЕ доказывает, что карточки не будет, и доказать это нечем:
      // поля web_page в Message нет вовсе. Это note, не deviation.
      notes.push(
        'link_preview_options в ответе отсутствует: наличие карточки не верифицируемо по ответу Bot API',
      );
    } else {
      // 8а. Поле за полем, но НЕ все пять всегда: набор сравниваемых полей зависит от того,
      // задавали ли мы `url` явно. Отсутствующее в ответе читается как дефолт Bot API,
      // а не как «совпало».
      //
      // ПОЧЕМУ ТАК — проверено на живом канале 25.08.2026, message_id 2.
      // Мы отправили `{is_disabled:false, url:null, prefer_*:false, show_above_text:false}`,
      // сервер вернул `{url:"https://geoboost.ru/guide"}` — то есть ПОДСТАВИЛ ссылку,
      // которую сам выбрал своим `get_first_url` по тексту сообщения. Сравнение «поле за
      // полем» объявляло это расхождением, и каждый пост со ссылкой без явного `url`
      // (то есть обычный пост) поднимал бы critical-алерт `publish_deviation`. Дежурный,
      // получающий critical на каждый пост, через неделю перестаёт их читать — цена такой
      // проверки отрицательная.
      //
      // Разделение опирается на два уже записанных правила, а не на новое соглашение:
      //   ПРАВИЛО 5.2  — `prefer_*` сервер ИГНОРИРУЕТ, если `url` не задан явно; сравнивать
      //                  то, что заведомо игнорируется, бессмысленно;
      //   ПРАВИЛО 6.2  — карточка превью по ответу Bot API не верифицируема вовсе (поля
      //                  `web_page` в `Message` нет), и выданный сервером `url` — его
      //                  собственный выбор, а не наш байт.
      //
      // `is_disabled` сравнивается ВСЕГДА и строго: «просили не показывать карточку, а она
      // появилась» — настоящее расхождение с одобренным, и единственное здесь проверяемое.
      //
      // Свой предсказатель (`getFirstUrl`) в сравнении НЕ участвует намеренно: вердикт
      // сверки не имеет права зависеть от версии нашего порта TDLib (§Хребет 3.5).
      // Возвращённая ссылка кладётся в notes — как свидетельство, а не как приговор.
      const want = canonical.link_preview_options;
      const wantRec = want === null ? LPO_DEFAULTS : (want as Record<string, unknown>);
      const pinnedUrl = typeof wantRec['url'] === 'string' && wantRec['url'] !== '';
      const compared = pinnedUrl ? LPO_FIELDS : (['is_disabled'] as const);

      for (const f of compared) {
        const expectedV = wantRec[f];
        const rawV = (got as Record<string, unknown>)[f];
        const actualV = rawV === undefined ? LPO_DEFAULTS[f] : rawV;
        if (expectedV !== actualV) {
          deviations.push({
            kind: 'link_preview_mismatch',
            expected: { [f]: expectedV },
            actual: { [f]: actualV },
          });
        }
      }

      if (!pinnedUrl) {
        const serverUrl = (got as Record<string, unknown>)['url'];
        if (typeof serverUrl === 'string' && serverUrl !== '') {
          notes.push(
            `link_preview_options.url не задавался; сервер выбрал ссылку сам: ${serverUrl}`,
          );
        }
      }
    }
  }

  return { ok: deviations.length === 0, deviations, notes, first_diff_at: firstDiffAt };
}
